import {
  createContext,
  use,
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { tabbable } from "tabbable";

import { Overlay } from "./overlay";

type DialogLabel = string | { labelledBy: string };

type DialogContextValue = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  triggerLabel: string;
  panelLabel: DialogLabel;
  triggerRef: RefObject<HTMLButtonElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext() {
  const ctx = use(DialogContext);
  if (!ctx) throw new Error("Dialog.* must be used within <Dialog>");
  return ctx;
}

type DialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Aria-label для кнопки-триггера. По умолчанию используется и для panel. */
  triggerLabel?: string;
  /** Если у panel есть видимый заголовок - `{ labelledBy: "id" }`. */
  panelLabel?: DialogLabel;
  children: ReactNode;
};

// Accessible модальная пара «кнопка-триггер <-> панель».
// Esc/клик вне закрывают, фокус ездит между trigger и panel, Tab внутри закольцован.
// Использование:
//   <Dialog isOpen={isOpen} setIsOpen={setIsOpen} triggerLabel={t("Настройки")}>
//     <DialogTrigger className={css.trigger}><GearIcon /></DialogTrigger>
//     <DialogPanel className={css.popover}>…</DialogPanel>
//   </Dialog>
export function Dialog({
  isOpen,
  setIsOpen,
  triggerLabel = "",
  panelLabel = triggerLabel,
  children,
}: DialogProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // При открытии переносим фокус в panel (она в портале - без явного focus
  // клавиатура осталась бы на trigger в основном дереве); при закрытии - обратно.
  // Эффект живёт в Dialog (а не в DialogPanel), чтобы корректно отрабатывал
  // и для панелей с условным рендером ({isOpen && <DialogPanel>}).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!wasOpen.current && isOpen) {
      panelRef.current?.focus({ preventScroll: true });
    } else if (wasOpen.current && !isOpen) {
      triggerRef.current?.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  return (
    <DialogContext
      value={{
        isOpen,
        setIsOpen,
        triggerLabel,
        panelLabel,
        triggerRef,
        panelRef,
      }}
    >
      {children}
    </DialogContext>
  );
}

type DialogTriggerProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-haspopup" | "aria-expanded" | "aria-label" | "title" | "type" | "ref"
>;

export function DialogTrigger({ onClick, ...props }: DialogTriggerProps) {
  const { isOpen, setIsOpen, triggerLabel, triggerRef } = useDialogContext();
  return (
    <button
      type="button"
      aria-label={triggerLabel}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      title={triggerLabel}
      {...props}
      ref={triggerRef}
      onClick={e => {
        onClick?.(e);
        if (!e.defaultPrevented) setIsOpen(!isOpen);
      }}
    />
  );
}

// Backdrop, который темнит фон. Портален в body — иначе position:fixed
// схлопывается до размеров header'а в текущей вёрстке.
export function DialogOverlay({ className }: { className?: string }) {
  const { isOpen, setIsOpen } = useDialogContext();
  return createPortal(
    <Overlay
      open={isOpen}
      onClick={() => setIsOpen(false)}
      className={className}
    />,
    document.body
  );
}

type DialogPanelProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "role" | "aria-modal" | "aria-label" | "aria-labelledby" | "tabIndex" | "ref"
>;

export function DialogPanel({ children, ...props }: DialogPanelProps) {
  const { isOpen, setIsOpen, panelLabel, panelRef } = useDialogContext();

  // Esc и focus-trap слушаем на самой panel, а не на window: keydown
  // приходит сюда только когда фокус внутри. Это автоматически решает
  // проблему вложенности (меню в портале не триггерит этот handler —
  // оно само разбирается со своим Esc).
  useEffect(() => {
    const panel = panelRef.current;
    if (isOpen && panel) {
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
          setIsOpen(false);
          return;
        }
        if (e.key !== "Tab") return;
        const tabbables = tabbable(panel!);
        if (tabbables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = tabbables[0];
        const last = tabbables[tabbables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === panel)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
      panel.addEventListener("keydown", handleKeyDown);
      return () => panel.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, panelRef, setIsOpen]);

  const labelAttrs =
    typeof panelLabel === "string"
      ? { "aria-label": panelLabel }
      : { "aria-labelledby": panelLabel.labelledBy };

  return createPortal(
    <div
      {...props}
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      {...labelAttrs}
      tabIndex={-1}
      inert={!isOpen}
    >
      {children}
    </div>,
    document.body
  );
}
